import { useCallback } from 'react'

import { AttentionStatusesBreadcrumb } from './components/attentionStatusesBreadcrumb'
import { AttentionStatusesFullscreen } from './components/attentionStatusesFullscreen'
import { useCockpitAttentionStatuses } from './hooks/useCockpitAttentionStatuses'
import { useManagerAttentionStatuses } from './hooks/useManagerAttentionStatuses'
import type { IAttentionStatusesTile, IAttentionStatusesVariantProps } from './types'
import { AttentionStatusesHero } from './variants/AttentionStatusesHero'

import styles from './CockpitAttentionStatusesCollapsed.module.scss'

interface ICollapsedBodyProps {
  tile: IAttentionStatusesTile
  onExpand: () => void
  /** Whose next steps each status lists under it, on the status level. */
  nextSteps?: IAttentionStatusesVariantProps['nextSteps']
}

const CollapsedBody = ({ tile, onExpand, nextSteps }: ICollapsedBodyProps) => {
  const { crumbs, items, selectItem, goToLevel } = tile

  /* Only a detail needs the room the expanded tile gives; every level above it is walked here, in
     the summary tile. Which rows open a detail is a per-row answer — a geo group of one partner
     opens that partner straight away. */
  const handleSelectItem = useCallback(
    (id: string) => {
      selectItem(id)

      if (items.find((item) => item.id === id)?.opensDetail) {
        onExpand()
      }
    },
    [items, onExpand, selectItem]
  )

  return (
    <div className={styles.root}>
      {crumbs.length > 0 && (
        <AttentionStatusesBreadcrumb
          crumbs={crumbs.slice(0, -1)}
          current={crumbs[crumbs.length - 1].label}
          onNavigate={goToLevel}
        />
      )}

      <AttentionStatusesHero
        items={tile.items}
        totalCount={tile.totalCount}
        unit={tile.unit}
        levelSeverity={tile.levelSeverity}
        severityCounts={tile.severityCounts}
        onSelectItem={handleSelectItem}
        nextSteps={crumbs.length === 0 ? nextSteps : undefined}
        onOpenPartnerDetail={onExpand}
      />
    </div>
  )
}

const SinglePartnerCollapsed = ({ onExpand }: { onExpand: () => void }) => {
  const tile = useCockpitAttentionStatuses()

  return <CollapsedBody tile={tile} onExpand={onExpand} nextSteps="partner" />
}

interface IManagerCollapsedProps {
  onExpand: () => void
  title: string
}

/* The collapsed tile stays mounted while the tile is expanded, so the full-screen dialog lives
   here and opens once, whichever header asked for it. */
const ManagerCollapsed = ({ onExpand, title }: IManagerCollapsedProps) => {
  const tile = useManagerAttentionStatuses()

  return (
    <>
      <CollapsedBody tile={tile} onExpand={onExpand} nextSteps="manager" />
      <AttentionStatusesFullscreen title={title} />
    </>
  )
}

interface ICockpitAttentionStatusesCollapsedProps {
  onExpand: () => void
  /** Manager cockpit: statuses are counted across the partners currently shown. */
  isManagerCockpitMode?: boolean
  /** The tile's title, repeated over the full-screen dialog. */
  title?: string
}

/**
 * The tile's summary state. Picking a row stores it and expands the tile, which is the same
 * transition the tile's own expand control performs.
 *
 * The two cockpits read different slices of the store, so each mode has its own component around
 * one shared body — a hook cannot be picked per render.
 */
export const CockpitAttentionStatusesCollapsed = ({
  onExpand,
  isManagerCockpitMode = false,
  title = '',
}: ICockpitAttentionStatusesCollapsedProps) =>
  isManagerCockpitMode ? (
    <ManagerCollapsed onExpand={onExpand} title={title} />
  ) : (
    <SinglePartnerCollapsed onExpand={onExpand} />
  )

CockpitAttentionStatusesCollapsed.displayName = 'CockpitAttentionStatusesCollapsed'
